import { snapBlockPrimaryVars } from '../../components/Block/Block.snap.palette';
import {
  snapButtonPrimaryVars,
  snapButtonSecondaryVars,
} from '../../components/Button/Button.snap.palette';
import { snapCarouselV3PrimaryVars } from '../../components/CarouselV3/CarouselV3.snap.palette';
import { snapCookieComponentsPrimaryVars } from '../../components/CookieComponents/CookieComponents.snap.palette';
import { snapDropdownMenuPrimaryVars } from '../../components/DropdownMenu/DropdownMenu.snap.palette';
import { FontFamily } from '../../components/Font/fontConstants';
import { snapFooterPrimaryVars } from '../../components/FooterV2/Footer.snap.palette';
import { snapFootnoteBlockPrimaryVars } from '../../components/FootnoteBlock/FootnoteBlock.snap.palette';
import { snapFormPrimaryVars } from '../../components/Form/Form.snap.palette';
import { snapHeroPrimaryVars } from '../../components/Hero/Hero.snap.palette';
import { snapMediaVars } from '../../components/Media/Media.snap.palette';
import { snapQuotePrimaryVars } from '../../components/QuoteV2/QuoteV2.snap.palette';
import { snapSideNavigationPrimaryVars } from '../../components/SideNavigation/SideNavigation.snap.palette';
import { snapStatsFontVars } from '../../components/Stats/Stats.snap.palette';
import { snapSubNavigationPrimaryVars } from '../../components/SubNavigation/SubNavigation.snap.palette';
import { snapSummaryCardPrimaryVars } from '../../components/SummaryCard/SummaryCard.snap.palette';
import { snapToggleSliderPrimaryVars } from '../../components/ToggleSlider/ToggleSlider.snap.palette';
import { BackgroundColor } from '../../constants';
import { defaultMotifWhite } from '../defaultMotifWhite';
import { m } from '../m';
import { MotifComponent, MotifScheme } from '../motifConstants';
import type { Motif } from '../motifTypes';
import { mergeMotifs } from '../motifUtils';

// TODO: export these values from SDS-M
const snapNeutralColors = {
  '--palette-gray-v50': '#F8F8F8',
  '--palette-gray-v100': '#F1F1F1',
  '--palette-gray-v150': '#D9D9D9',
  '--palette-gray-v200': '#BFBFBF',
  '--palette-gray-v250': '#A6A6A6',
  '--palette-gray-v300': '#8C8C8C',
  '--palette-black-v50': '#757575',
  '--palette-black-v100': '#595959',
  '--palette-black-v125': '#404040',
  '--palette-black-v150': '#262626',
};

/** Motif for the Snap.com homepage */
export const snapMotif: Motif = mergeMotifs(defaultMotifWhite, {
  name: 'Snap',
  // we need to manually override this later because the merge function does not replace the array, it merges them.
  fontFamily: [FontFamily.INTER],
  [MotifScheme.DEFAULT]: {
    legacyName: BackgroundColor.White,
    name: 'Snap White background',
    [MotifComponent.ROOT]: {
      ...snapNeutralColors,
      '--font-family': `${FontFamily.INTER}, Helvetica, Tahoma, Arial, sans-serif`,
      // H1
      '--h1-font-family': `${FontFamily.INTER}, Helvetica, Tahoma, Arial, sans-serif`,
      '--h1-desktop-font-size': '56px',
      '--h1-desktop-font-size-compact': '48px',
      '--h1-desktop-font-line-height': '120%',
      '--h1-desktop-font-weight': '500',
      '--h1-desktop-font-stretch': 'normal',
      '--h1-desktop-font-text-transform': 'none',
      '--h1-mobile-font-size': '32px',
      '--h1-mobile-font-line-height': '120%',
      '--h1-mobile-font-weight': '500',
      '--h1-mobile-font-stretch': 'normal',
      '--h1-mobile-font-text-transform': 'none',
      // H2
      '--h2-font-family': `${FontFamily.INTER}, Helvetica, Tahoma, Arial, sans-serif`,
      '--h2-desktop-font-size': '36px',
      '--h2-desktop-font-line-height': '120%',
      '--h2-desktop-font-weight': '500',
      '--h2-desktop-font-stretch': 'normal',
      '--h2-desktop-font-text-transform': 'none',
      '--h2-desktop-font-letter-spacing': '0',
      '--h2-mobile-font-size': '28px',
      '--h2-mobile-font-line-height': '120%',
      '--h2-mobile-font-weight': '500',
      '--h2-mobile-font-stretch': 'normal',
      '--h2-mobile-font-text-transform': 'none',
      '--h2-mobile-font-letter-spacing': '0',
      // H3
      '--h3-font-family': `${FontFamily.INTER}, Helvetica, Tahoma, Arial, sans-serif`,
      '--h3-desktop-font-size': '28px',
      '--h3-desktop-font-weight': '500',
      '--h3-mobile-font-size': '24px',
      '--h3-mobile-font-weight': '500',
      // H4
      '--h4-font-family': `${FontFamily.INTER}, Helvetica, Tahoma, Arial, sans-serif`,
      '--h4-desktop-font-size': '22px',
      '--h4-desktop-font-weight': '500',
      '--h4-mobile-font-size': '22px',
      '--h4-mobile-font-weight': '500',
      // H5
      '--h5-font-family': `${FontFamily.INTER}, Helvetica, Tahoma, Arial, sans-serif`,
      '--h5-desktop-font-size': '20px',
      '--h5-desktop-font-weight': '500',
      '--h5-mobile-font-size': '18px',
      '--h5-mobile-font-weight': '500',
      // H6
      '--h6-font-family': `${FontFamily.INTER}, Helvetica, Tahoma, Arial, sans-serif`,
      '--h6-desktop-font-weight': '500',
      '--h6-mobile-font-size': '16px',
      '--h6-mobile-font-weight': '500',
      // action colors
      '--action-text-hover-color': m('--neutral-v700'),
      '--action-indicator-hover-opacity': '0',
      '--action-indicator-border-radius': '0px',
      // curtain effect variables
      '--curtain-backdrop-filter': 'blur(6px)',
      '--curtain-color': m('--neutral-v0'),
      '--curtain-opacity': '0.90',
      // override the gutter color to be white
      '--gutter-color': m('--neutral-v100'),
      // stats font
      ...snapStatsFontVars,
    },
    [MotifComponent.BLOCK]: snapBlockPrimaryVars,
    [MotifComponent.BUTTON]: snapButtonPrimaryVars,
    [MotifComponent.CAROUSEL]: snapCarouselV3PrimaryVars,
    [MotifComponent.COOKIE_MODAL_SETTINGS]: snapCookieComponentsPrimaryVars,
    [MotifComponent.DROPDOWN_MENU]: snapDropdownMenuPrimaryVars,
    [MotifComponent.FOOTER]: snapFooterPrimaryVars,
    [MotifComponent.FOOTNOTE]: snapFootnoteBlockPrimaryVars,
    [MotifComponent.FORM]: snapFormPrimaryVars,
    [MotifComponent.HEADER]: {
      '--global-header-bg-color': m('--neutral-v0'),
      '--global-header-border-color': m('--neutral-v700'),
      '--global-header-nav-screen-global-links-bg-color': m('--neutral-v0'),
      '--global-header-navigator-item-hover-color': m('--action-text-hover-color'),
      '--global-header-navigator-item-desktop-font-text-transform': 'uppercase',
      '--global-header-navigator-item-mobile-font-text-transform': 'none',
    },
    [MotifComponent.HERO]: snapHeroPrimaryVars,
    [MotifComponent.MEDIA]: snapMediaVars,
    [MotifComponent.QUOTE]: snapQuotePrimaryVars,
    [MotifComponent.SIDE_NAVIGATION]: snapSideNavigationPrimaryVars,
    [MotifComponent.SUB_NAVIGATION]: snapSubNavigationPrimaryVars,
    [MotifComponent.SUMMARY_CARD]: snapSummaryCardPrimaryVars,
    [MotifComponent.TOGGLE_SLIDER]: snapToggleSliderPrimaryVars,
  },
  [MotifScheme.SECONDARY]: {
    name: 'Snap Secondary',
    [MotifComponent.BUTTON]: snapButtonSecondaryVars,
  },
  [MotifScheme.TERTIARY]: {
    name: 'Snap Tertiary',
    [MotifComponent.BUTTON]: snapButtonPrimaryVars,
  },
});

// override the font family to use Inter
snapMotif.fontFamily = [FontFamily.INTER];
